import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileText, Plus, Trash2, Upload, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function InvoiceQuotationSettings() {
  const { token } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    terms_and_conditions_quotation: '',
    terms_and_conditions_invoice: '',
    payment_banks: []
  });
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/invoice-quotation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings/invoice-quotation`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice & Quotation settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addBank = () => {
    const newBank = {
      id: Date.now().toString(),
      bank_name: '',
      bank_logo: '',
      accounts: [
        { currency: 'MVR', account_number: '' },
        { currency: 'USD', account_number: '' },
        { currency: 'CHN', account_number: '' }
      ]
    };
    setSettings({
      ...settings,
      payment_banks: [...settings.payment_banks, newBank]
    });
  };

  const removeBank = (bankId) => {
    setSettings({
      ...settings,
      payment_banks: settings.payment_banks.filter(bank => bank.id !== bankId)
    });
  };

  const updateBank = (bankId, field, value) => {
    setSettings({
      ...settings,
      payment_banks: settings.payment_banks.map(bank =>
        bank.id === bankId ? { ...bank, [field]: value } : bank
      )
    });
  };

  const updateBankAccount = (bankId, currency, accountNumber) => {
    setSettings({
      ...settings,
      payment_banks: settings.payment_banks.map(bank =>
        bank.id === bankId
          ? {
              ...bank,
              accounts: bank.accounts.map(acc =>
                acc.currency === currency ? { ...acc, account_number: accountNumber } : acc
              )
            }
          : bank
      )
    });
  };

  const handleLogoUpload = async (bankId, file) => {
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo size should be less than 2MB');
      return;
    }

    try {
      setUploadingLogo(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        updateBank(bankId, 'bank_logo', reader.result);
        toast.success('Logo uploaded successfully');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Invoice & Quotation Settings</h1>
          <p className="text-slate-600 mt-1">Configure terms, conditions, and payment methods</p>
        </div>

        <div className="space-y-6">
          {/* Terms and Conditions */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-emerald-600" />
              <h2 className="text-xl font-bold text-slate-900">Terms & Conditions</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Quotation Terms & Conditions</Label>
                <textarea
                  value={settings.terms_and_conditions_quotation}
                  onChange={(e) => setSettings({ ...settings, terms_and_conditions_quotation: e.target.value })}
                  className="w-full min-h-[120px] px-3 py-2 text-sm border rounded-md"
                  placeholder="Enter terms and conditions for QUOTATIONS...
Example:
• Valid for 30 days
• Prices subject to change
• 50% advance payment required"
                />
                <p className="text-xs text-slate-500 mt-1">
                  This will appear only on quotations
                </p>
              </div>

              <div>
                <Label>Invoice Terms & Conditions</Label>
                <textarea
                  value={settings.terms_and_conditions_invoice}
                  onChange={(e) => setSettings({ ...settings, terms_and_conditions_invoice: e.target.value })}
                  className="w-full min-h-[120px] px-3 py-2 text-sm border rounded-md"
                  placeholder="Enter terms and conditions for INVOICES...
Example:
• Payment due within 30 days
• Late payment fee: 2% per month
• All sales are final"
                />
                <p className="text-xs text-slate-500 mt-1">
                  This will appear only on invoices
                </p>
              </div>
            </div>
          </Card>

          {/* Payment Methods */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-emerald-600" />
                <h2 className="text-xl font-bold text-slate-900">Payment Methods</h2>
              </div>
              <Button onClick={addBank} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Bank
              </Button>
            </div>

            <div className="space-y-6">
              {settings.payment_banks.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <p>No payment methods added yet</p>
                  <p className="text-sm mt-1">Click "Add Bank" to add your first bank account</p>
                </div>
              ) : (
                settings.payment_banks.map((bank, index) => (
                  <div key={bank.id} className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-start justify-between">
                      <h3 className="font-semibold text-slate-900">Bank {index + 1}</h3>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => removeBank(bank.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Bank Name */}
                    <div>
                      <Label>Bank Name</Label>
                      <Input
                        value={bank.bank_name}
                        onChange={(e) => updateBank(bank.id, 'bank_name', e.target.value)}
                        placeholder="e.g., Bank of Maldives"
                      />
                    </div>

                    {/* Bank Logo */}
                    <div>
                      <Label>Bank Logo</Label>
                      <div className="flex items-center gap-4">
                        {bank.bank_logo && (
                          <div className="border rounded p-2">
                            <img 
                              src={bank.bank_logo} 
                              alt="Bank Logo" 
                              className="h-16 w-auto object-contain"
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleLogoUpload(bank.id, e.target.files[0])}
                            className="hidden"
                            id={`bank-logo-${bank.id}`}
                          />
                          <label htmlFor={`bank-logo-${bank.id}`}>
                            <Button
                              type="button"
                              variant="outline"
                              disabled={uploadingLogo}
                              onClick={() => document.getElementById(`bank-logo-${bank.id}`).click()}
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              {uploadingLogo ? 'Uploading...' : bank.bank_logo ? 'Change Logo' : 'Upload Logo'}
                            </Button>
                          </label>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Max size: 2MB. Recommended: Square logo for best results
                      </p>
                    </div>

                    {/* Currency Accounts */}
                    <div>
                      <Label className="mb-2 block">Bank Accounts (by Currency)</Label>
                      <div className="space-y-3">
                        {bank.accounts.map((account) => (
                          <div key={account.currency} className="flex items-center gap-3">
                            <div className="w-16 font-semibold text-slate-700">
                              {account.currency}
                            </div>
                            <Input
                              value={account.account_number}
                              onChange={(e) => updateBankAccount(bank.id, account.currency, e.target.value)}
                              placeholder={`Enter ${account.currency} account number`}
                              className="flex-1"
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Leave blank if bank doesn't support a particular currency
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-lg"
          >
            <Save className="w-5 h-5 mr-2" />
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
